import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from "contentful";
import type { TypeSmallCtaSkeleton } from "./TypeSmallCta";
import type { TypeTableOfContentsSettingSkeleton } from "./TypeTableOfContentsSetting";
import type { TypeTestimonialSkeleton } from "./TypeTestimonial";

/**
 * Fields type definition for content type 'TypeTestimonialSection'
 * @name TypeTestimonialSectionFields
 * @type {TypeTestimonialSectionFields}
 * @memberof TypeTestimonialSection
 */
export interface TypeTestimonialSectionFields {
    /**
     * Field type definition for field 'internalTitle' (Internal Title)
     * @name Internal Title
     * @localized false
     */
    internalTitle?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'header' (Header)
     * @name Header
     * @localized true
     */
    header?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'subHeader' (Sub Header)
     * @name Sub Header
     * @localized true
     */
    subHeader?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'backgroundColor' (BackgroundColor)
     * @name BackgroundColor
     * @localized false
     */
    backgroundColor: EntryFieldTypes.Symbol<"grey" | "light-blue" | "light-grey" | "white">;
    /**
     * Field type definition for field 'isStacked' (Is Stacked)
     * @name Is Stacked
     * @localized false
     */
    isStacked?: EntryFieldTypes.Boolean;
    /**
     * Field type definition for field 'testimonials' (Testimonials)
     * @name Testimonials
     * @localized false
     */
    testimonials?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeTestimonialSkeleton>>;
    /**
     * Field type definition for field 'tocSetting' (tocSetting)
     * @name tocSetting
     * @localized false
     */
    tocSetting?: EntryFieldTypes.EntryLink<TypeTableOfContentsSettingSkeleton>;
    /**
     * Field type definition for field 'cta' (CTA)
     * @name CTA
     * @localized true
     */
    cta?: EntryFieldTypes.EntryLink<TypeSmallCtaSkeleton>;
}

/**
 * Entry skeleton type definition for content type 'testimonialSection' (Testimonial Section)
 * @name TypeTestimonialSectionSkeleton
 * @type {TypeTestimonialSectionSkeleton}
 * @author 7BjsgI9gzpO6Dv7BH5AOT6
 * @since 2018-03-23T19:29:29.076Z
 * @version 23
 */
export type TypeTestimonialSectionSkeleton = EntrySkeletonType<TypeTestimonialSectionFields, "testimonialSection">;
/**
 * Entry type definition for content type 'testimonialSection' (Testimonial Section)
 * @name TypeTestimonialSection
 * @type {TypeTestimonialSection}
 * @author 7BjsgI9gzpO6Dv7BH5AOT6
 * @since 2018-03-23T19:29:29.076Z
 * @version 23
 */
export type TypeTestimonialSection<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<TypeTestimonialSectionSkeleton, Modifiers, Locales>;

export function isTypeTestimonialSection<Modifiers extends ChainModifiers, Locales extends LocaleCode>(entry: Entry<EntrySkeletonType, Modifiers, Locales>): entry is TypeTestimonialSection<Modifiers, Locales> {
    return entry.sys.contentType.sys.id === 'testimonialSection'
}
